import { effect, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { AuthService } from './auth.service';
import { BrowserNotificationService } from './browser-notification.service';
import { ThreadEventsService } from './thread-events.service';

/** Backend payload for a browser reach-out — display fields only (ADR-0018). */
export type ReachOutBrowserEvent = {
    type: 'reach_out_browser';
    title: string | null;
    body: string;
    persona_id: string | null;
    persona_display_name: string | null;
    room_id: string;
};

/**
 * Browser delivery of DDR reach-outs (ADR-0018). Listens app-wide on the
 * shared SSE stream for `reach_out_browser` events and renders each as a
 * browser Notification; clicking it navigates to the DDR's DM room, where the
 * durable record already sits with its unread badge (the replay for a missed
 * notification — there is no delivery queue and no web push).
 *
 * Eagerly injected in the app root: no other SSE subscriber is guaranteed
 * alive outside the chat surfaces, and a reach-out must ping on any route.
 */
@Injectable({ providedIn: 'root' })
export class ReachOutNotificationsService {
    private readonly auth = inject(AuthService);
    private readonly threadEvents = inject(ThreadEventsService);
    private readonly notifications = inject(BrowserNotificationService);
    private readonly router = inject(Router);

    private sseSub: Subscription | null = null;

    // noinspection JSUnusedLocalSymbols
    private readonly _onAuth = effect(() => {
        const isAuthed = !!this.auth.state().user && this.auth.ddrEnabled();
        if (isAuthed) {
            this.start();
        } else {
            this.stop();
        }
    });

    start(): void {
        if (this.sseSub) {
            return;
        }
        this.sseSub = this.threadEvents.connect().subscribe(evt => {
            // reach_out_browser is not part of the ThreadEvent union (like
            // proactive_activity, it is consumer-filtered), hence the cast.
            const e = evt as unknown as Partial<ReachOutBrowserEvent> & { type?: string };
            if (e.type === 'reach_out_browser' && typeof e.room_id === 'string' && typeof e.body === 'string') {
                this.show(e as ReachOutBrowserEvent);
            }
        });
    }

    stop(): void {
        this.sseSub?.unsubscribe();
        this.sseSub = null;
    }

    private show(event: ReachOutBrowserEvent): void {
        // The server only publishes when the effective channel is Browser
        // alerts, but permission is browser-local truth — never render
        // without it.
        if (this.notifications.permission() !== 'granted') {
            return;
        }
        const personaId = event.persona_id || (event.room_id.startsWith('dm-') ? event.room_id.slice(3) : null);
        const heading = event.title || (event.persona_display_name ? `${event.persona_display_name} reached out` : 'Your DDR reached out');
        // One tag per DM room: a rapid follow-up replaces the previous
        // notification instead of stacking.
        const notification = this.createNotification(heading, { body: event.body, tag: `reach-out-${event.room_id}` });
        if (!notification || !personaId) {
            return;
        }
        notification.onclick = () => {
            window.focus();
            void this.router.navigate(['/chat/ddr/dm', personaId]);
            notification.close();
        };
    }

    /** Page-created Notification (not the service worker): it is the only
     *  path that supports an onclick navigation handler — ngsw has no
     *  notification-click plumbing wired in this app. Seam for specs. */
    createNotification(title: string, options: NotificationOptions): Notification | null {
        if (!('Notification' in window)) {
            return null;
        }
        try {
            return new Notification(title, options);
        } catch {
            // new Notification() throws on platforms that require the SW path
            // (iOS Safari); the DM record's unread badge covers the miss.
            return null;
        }
    }
}
